interface ShareOptions {
  url: string;
  title?: string;
  text?: string;
}

interface ShareResult {
  success: boolean;
  method: "native" | "clipboard" | "none";
  message?: string;
}

/**
 * Share a URL using the Web Share API if available, otherwise copy to clipboard.
 *
 * @param options - The share options (url, title, text)
 * @returns ShareResult with success status, method used, and optional message
 */
export async function shareUrl(options: ShareOptions): Promise<ShareResult> {
  const { url, title, text } = options;

  // Try native Web Share API first
  if (navigator.share) {
    try {
      await navigator.share({ url, title, text });
      return { success: true, method: "native" };
    } catch (error) {
      // User cancelled or share failed - fall through to clipboard
      if (error instanceof Error && error.name === "AbortError") {
        // User cancelled, don't show any message
        return { success: false, method: "none" };
      }
      // Other error, fall through to clipboard fallback
    }
  }

  // Fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(url);
    return { success: true, method: "clipboard", message: "Link copied to clipboard" };
  } catch {
    return { success: false, method: "none", message: "Failed to copy link" };
  }
}
